import { API_CONFIG } from '../lib/apiConfig';
import { parseCsv } from '../lib/csv';
import { createCsvStatsHook } from '../lib/hookFactories';
import {
  PerCrownDiameterClass,
  PerCrownDiameterDistribution,
  PerCrownForestType,
  PerCrownHeightBin,
  PerCrownStats,
} from '../lib/types';

// CSV schema (long format — one row per stat group). The same CSV is the
// canonical S3 artifact for both the left-panel and (indirectly) the
// insight-card precompute step.
//
//   group_kind,group_id,group_label,count,total_tree_biomass_mg,above_ground_biomass_mg,acres
//
// Row meanings:
//
//   summary,all          Bottom-up shed-wide totals over crowns with
//                        fit_level != 5. count = treeCount; total_tree_*
//                        and above_ground_* are the two biomass totals;
//                        acres = covered shed acres.
//
//   summary,all_total    Unfiltered context. count = treeCountTotal;
//                        total_tree_biomass_mg overloaded as sawtimberCount
//                        so the schema stays flat. (The schema only has
//                        one numeric "count-like" column per row.)
//
//   summary,top_down     Reference top-down AGB integrated from the AGB
//                        100m raster. Carried in above_ground_biomass_mg.
//                        Lets the card show bottom-up vs top-down without a
//                        second fetch.
//
//   diameter,<cls>       One per FIA diameter class
//                        (sapling/poletimber/small_saw/large_saw).
//
//   height,<bin>         One per height bin (0_to_5m, 5_to_15m, ..., 35m_plus).
//                        group_label is the chart-axis label ("0–5 m" etc.).
//
//   fortypcd,<code>      One per FIA forest-type code in the rollup.
//                        group_label is the resolved FIA name when known,
//                        else "FIA #<code>".
//
//   fit_level,5          Crowns whose allometric cascade fell to spatial
//                        fallback. count = fitLevel5Count.
//
// Unknown group_kind rows are dropped silently. Missing numerics zero out.

const DIAMETER_ORDER: PerCrownDiameterClass[] = [
  'sapling',
  'poletimber',
  'small_sawtimber',
  'large_sawtimber',
];

const num = (s: string | undefined): number => {
  if (s === undefined || s === '') return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

const parsePerCrownStats = (text: string): PerCrownStats | null => {
  const rows = parseCsv(text);
  if (rows.length === 0) return null;

  let totalAcres = 0;
  let treeCount = 0;
  let treeCountTotal = 0;
  let sawtimberCount = 0;
  let fitLevel5Count = 0;
  let totalTreeBiomassMg = 0;
  let aboveGroundBiomassMg = 0;
  let topDownAgbMg: number | null = null;

  const diameterMap = new Map<PerCrownDiameterClass, PerCrownDiameterDistribution>();
  for (const cls of DIAMETER_ORDER) {
    diameterMap.set(cls, { cls, count: 0, totalTreeBiomassMg: 0 });
  }
  const heightBins: PerCrownHeightBin[] = [];
  const forestTypes: PerCrownForestType[] = [];

  for (const row of rows) {
    const kind = row['group_kind'];
    const id = row['group_id'];
    const label = row['group_label'] ?? '';
    const count = num(row['count']);
    const tt = num(row['total_tree_biomass_mg']);
    const ag = num(row['above_ground_biomass_mg']);
    const acres = num(row['acres']);

    if (kind === 'summary' && id === 'all') {
      treeCount = Math.round(count);
      totalTreeBiomassMg = tt;
      aboveGroundBiomassMg = ag;
      totalAcres = acres;
    } else if (kind === 'summary' && id === 'all_total') {
      treeCountTotal = Math.round(count);
      sawtimberCount = Math.round(tt);
    } else if (kind === 'summary' && id === 'top_down') {
      topDownAgbMg = ag > 0 ? ag : null;
    } else if (kind === 'diameter') {
      const cls = id as PerCrownDiameterClass;
      const slot = diameterMap.get(cls);
      if (slot) {
        slot.count = Math.round(count);
        slot.totalTreeBiomassMg = tt;
      }
    } else if (kind === 'height') {
      heightBins.push({ bin: id, label: label || id, count: Math.round(count) });
    } else if (kind === 'fortypcd') {
      const fortypcd = Number(id);
      if (Number.isFinite(fortypcd)) {
        forestTypes.push({
          fortypcd,
          name: label || `FIA #${fortypcd}`,
          count: Math.round(count),
          totalTreeBiomassMg: tt,
        });
      }
    } else if (kind === 'fit_level' && id === '5') {
      fitLevel5Count = Math.round(count);
    }
  }

  const diameterClasses = DIAMETER_ORDER.map((cls) =>
    diameterMap.get(cls) ?? { cls, count: 0, totalTreeBiomassMg: 0 },
  );

  if (treeCount === 0 && treeCountTotal === 0 && totalTreeBiomassMg === 0) {
    return null;
  }

  forestTypes.sort((a, b) => b.count - a.count);

  return {
    totalAcres,
    treeCount,
    treeCountTotal,
    sawtimberCount,
    fitLevel5Count,
    totalTreeBiomassMg,
    aboveGroundBiomassMg,
    topDownAgbMg,
    diameterClasses,
    heightBins,
    forestTypes,
  };
};

/**
 * Per-shed aggregate stats for the per-crown forest attributes layer. CSV at
 * sfi/per_crown/{mill}_{dt}min_per_crown.csv. 404s on sheds outside Sierra
 * Nevada coverage; the left-panel surfaces a "no coverage" panel in that case.
 */
export const usePerCrownStats = createCsvStatsHook({
  path: API_CONFIG.s3.paths.perCrownStats,
  parse: parsePerCrownStats,
  errorPrefix: 'Failed to load per-crown forest attributes',
});

export { parsePerCrownStats };
